import { motion } from "framer-motion";
import { Users, Briefcase, ArrowUpRight } from "lucide-react";
import { Overline, Reveal } from "./Shared";
import { FLEET, waLink } from "../../lib/site";

const FleetCard = ({ car, index }) => (
  <Reveal delay={index * 0.08}>
    <div
      className={`group relative flex flex-col rounded-sm border border-white/10 bg-charcoal overflow-hidden ${
        car.featured ? "ring-1 ring-gold/40" : ""
      }`}
      data-testid={`fleet-card-${car.id}`}
    >
      <div className="relative aspect-[16/9] overflow-hidden bg-black sm:aspect-[16/10]">
        <img
          src={car.image}
          alt={`${car.tier} — ${car.model}`}
          className="h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-transparent to-transparent" />
        {car.featured && (
          <span className="absolute top-4 left-4 bg-gold text-ink text-[0.6rem] uppercase tracking-[0.25em] px-3 py-1.5 rounded-sm font-medium">
            Just Released
          </span>
        )}
        <span className="absolute top-4 right-4 font-heading text-white/90 text-lg">{car.tier}</span>
      </div>

      <div className="flex flex-1 flex-col p-5 md:p-8">
        <h3 className="font-heading text-xl md:text-3xl text-white">{car.model}</h3>
        <div className="mt-4 flex items-center gap-6 text-white/50 text-xs uppercase tracking-widest">
          <span className="flex items-center gap-2">
            <Users size={14} className="text-gold" /> {car.seats}
          </span>
          <span className="flex items-center gap-2">
            <Briefcase size={14} className="text-gold" /> {car.luggage}
          </span>
        </div>
        <p className="mt-5 text-sm text-white/60 leading-relaxed flex-1">{car.blurb}</p>

        <div className="mt-7 flex items-end justify-between border-t border-white/10 pt-6">
          <div>
            <span className="block text-[0.65rem] uppercase tracking-[0.3em] text-white/40">From</span>
            <span className="font-heading text-3xl text-gold">£{car.price}</span>
          </div>
          <a
            href={waLink(`Hello Upper Class Chauffeurs, I'd like to book the ${car.tier} — ${car.model}.`)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`fleet-book-${car.id}`}
            className="inline-flex items-center gap-2 text-sm text-white hover:text-gold transition-colors duration-300 group/btn"
          >
            Book Now
            <ArrowUpRight size={16} className="transition-transform duration-300 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
          </a>
        </div>
      </div>
    </div>
  </Reveal>
);

export const Fleet = () => (
  <section id="fleet" className="relative py-24 md:py-32 bg-ink" data-testid="fleet-section">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
        <div>
          <Overline>The Fleet</Overline>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="mt-5 font-heading font-light text-4xl md:text-6xl text-white leading-[1] max-w-2xl"
          >
            Four ways to travel <span className="italic text-gold">first class</span>
          </motion.h2>
        </div>
        <p className="text-white/55 max-w-sm text-sm leading-relaxed">
          A hand-picked fleet — none older than three years — matched precisely to your journey, party size and
          occasion.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
        {FLEET.map((car, i) => (
          <FleetCard key={car.id} car={car} index={i} />
        ))}
      </div>
    </div>
  </section>
);
